import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BarChart3, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (days) => {
  const base = new Date();
  base.setDate(base.getDate() + days);
  return formatDateInput(base);
};

const formatNumber = (value) => new Intl.NumberFormat('id-ID').format(value);

const KpiOverview = () => {
  const [complaints, setComplaints] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const dateTo = useMemo(() => shiftDate(0), []);
  const dateFrom = useMemo(() => shiftDate(-6), []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [complaintsRes, checklistRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/complaints`, config),
        axios.get(`${BACKEND_URL}/api/staff/housekeeping/checklists?date_from=${dateFrom}&date_to=${dateTo}`, config)
      ]);
      setComplaints(complaintsRes.data || []);
      setChecklists(checklistRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal memuat KPI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const statusCount = {
      new: complaints.filter((c) => c.status === 'new').length,
      in_progress: complaints.filter((c) => c.status === 'in_progress').length,
      closed: complaints.filter((c) => c.status === 'closed').length
    };
    const overdue = complaints.filter((c) => {
      if (c.status === 'closed' || !c.created_at) return false;
      const created = new Date(c.created_at);
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);
      return diffDays > 3;
    }).length;

    const closedComplaints = complaints.filter((c) => c.status === 'closed' && c.created_at && c.updated_at);
    const avgCloseDays = closedComplaints.length
      ? closedComplaints.reduce((sum, c) => {
          const created = new Date(c.created_at);
          const closed = new Date(c.updated_at);
          return sum + (closed - created) / (1000 * 60 * 60 * 24);
        }, 0) / closedComplaints.length
      : 0;

    const checklistTotal = checklists.length;
    const checklistCompleted = checklists.filter((c) => (c.items || []).length > 0 && (c.items || []).every((item) => item.done)).length;
    const checklistRate = checklistTotal ? Math.round((checklistCompleted / checklistTotal) * 100) : 0;

    const propertyMap = {};
    checklists.forEach((entry) => {
      const key = entry.property_name || 'Tanpa Properti';
      if (!propertyMap[key]) {
        propertyMap[key] = { total: 0, completed: 0 };
      }
      propertyMap[key].total += 1;
      if ((entry.items || []).length > 0 && (entry.items || []).every((item) => item.done)) {
        propertyMap[key].completed += 1;
      }
    });

    const propertyRows = Object.entries(propertyMap).map(([property, value]) => ({
      property,
      total: value.total,
      completed: value.completed,
      rate: value.total ? Math.round((value.completed / value.total) * 100) : 0
    }));

    return {
      statusCount,
      overdue,
      avgCloseDays,
      checklistTotal,
      checklistCompleted,
      checklistRate,
      propertyRows
    };
  }, [complaints, checklists]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-isk-text flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-isk-teal" /> KPI Operasional
          </h2>
          <p className="text-sm text-isk-muted mt-1">Ringkasan performa tim untuk periode {dateFrom} s.d {dateTo}</p>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <div className="glass-card rounded-2xl p-5">
          <p className="text-xs text-isk-muted">Komplain Baru</p>
          <p className="text-2xl font-bold text-isk-text">{formatNumber(stats.statusCount.new)}</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <p className="text-xs text-isk-muted">Komplain Diproses</p>
          <p className="text-2xl font-bold text-isk-text">{formatNumber(stats.statusCount.in_progress)}</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <p className="text-xs text-isk-muted">Komplain Selesai</p>
          <p className="text-2xl font-bold text-isk-text">{formatNumber(stats.statusCount.closed)}</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <div className="flex items-center gap-2 text-isk-muted">
            <AlertTriangle className="w-4 h-4" />
            <p className="text-xs">Komplain > 3 Hari</p>
          </div>
          <p className="text-2xl font-bold text-isk-text">{formatNumber(stats.overdue)}</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <div className="flex items-center gap-2 text-isk-muted">
            <Clock className="w-4 h-4" />
            <p className="text-xs">Rata-rata Selesai</p>
          </div>
          <p className="text-2xl font-bold text-isk-text">{stats.avgCloseDays.toFixed(1)} hari</p>
        </div>
        <div className="glass-card rounded-2xl p-5">
          <div className="flex items-center gap-2 text-isk-muted">
            <CheckCircle2 className="w-4 h-4" />
            <p className="text-xs">Checklist Selesai</p>
          </div>
          <p className="text-2xl font-bold text-isk-text">{stats.checklistRate}%</p>
          <p className="text-xs text-isk-muted mt-1">{stats.checklistCompleted}/{stats.checklistTotal} checklist</p>
        </div>
      </div>

      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-isk-text">Performa Housekeeping</h3>
            <p className="text-sm text-isk-muted">Ringkasan per properti (7 hari)</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-2 text-xs font-semibold text-isk-text">Properti</th>
                <th className="text-left py-3 px-2 text-xs font-semibold text-isk-text">Checklist</th>
                <th className="text-left py-3 px-2 text-xs font-semibold text-isk-text">Selesai</th>
                <th className="text-left py-3 px-2 text-xs font-semibold text-isk-text">Tingkat</th>
              </tr>
            </thead>
            <tbody>
              {stats.propertyRows.length > 0 ? stats.propertyRows.map((row) => (
                <tr key={row.property} className="border-b border-white/10">
                  <td className="py-3 px-2 text-sm text-isk-text">{row.property}</td>
                  <td className="py-3 px-2 text-sm text-isk-muted">{row.total}</td>
                  <td className="py-3 px-2 text-sm text-isk-muted">{row.completed}</td>
                  <td className="py-3 px-2 text-sm text-isk-text font-semibold">{row.rate}%</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="py-6 text-center text-isk-muted text-sm">Belum ada checklist pada periode ini.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KpiOverview;
